// Next.js API route support: https://nextjs.org/docs/api-routes/introduction
import cache from "@/pages/api/cache";

import {CONTENT_TYPES as DESIGN_CONTENT_TYPES} from "@/utils/constants";
import {CONTENT_TYPES as COMMON_CONTENT_TYPES, PLEO_TYPE} from '@repo/utils/constants';


const datoCmsApiToken = process.env.NEXT_DATO_API_TOKEN;
const input = `https://graphql.datocms.com/environments/${process.env.ENVIRONMENT}`;
const method = `POST`;
const next = {
    cache: 'force-cache'
};

const headers = {
    Authorization: `Bearer ${datoCmsApiToken}`,
    'Content-Type': 'application/json',
};

async function fetchDataAndCache(cacheKey, input, method, headers, next, body, contentKey, getFirstArrayItem) {
    const cachedResult = cache.get(cacheKey);
    if (cachedResult !== undefined) return cachedResult;
    const result = await fetch(input, {
        method,
        headers,
        next: {
            tags: [cacheKey],
        },
        cache: 'force-cache',
        body: JSON.stringify(body),
    });
    if (!result.ok) {
        console.error(result);
        return {};
    }
    const {data} = await result.json();
    const finalData = data && getFirstArrayItem ? data[contentKey][0] : data ? data[contentKey] : {};
    cache.set(cacheKey, finalData);
    return finalData;

}

export async function getPages(contentType, query, locale) {
    const cacheKey = `${contentType}-${locale}`;
    const body = {
        query,
        variables: {locale},
    };
    return await fetchDataAndCache(cacheKey, input, method, headers, next, body, contentType, false);
}

export async function getPageData(contentType, slug, query, locale) {
    const cacheKey = `${slug}-${locale}`;
    const body = {
        query,
        variables: {locale, slug},
    };
    return await fetchDataAndCache(cacheKey, input, method, headers, next, body, contentType, true);
}

export async function getGlobals(locale) {
    const key = 'global';
    const cacheKey = `${key}-${locale}`;
    const body = {
        query: `
            query GetGlobals($locale: SiteLocale) {
                ${key}(locale: $locale) {
                    id
					title
					pleoType
					address
					email
					phone
					title
					socials {
						id
					  title
					  link
					}
					modalHeader
					modalDescription
					buttonTextClose
					buttonTextOpen
					readArticleButtonText
					toTopButton
					cookiesButton
					projectElevatorText
					policyButton {
					  text
					  link{
						... on ${DESIGN_CONTENT_TYPES.SINGLE_PAGE}{
						  slug
						}
					  }
					}
                }
            }
        `,
        variables: {locale},
    };

    return await fetchDataAndCache(cacheKey, input, method, headers, next, body, key, false);
}

async function getMenu(locale) {
    const key = 'allMenus';
    const cacheKey = `${key}-${locale}`;
    const id = 'HvI7slMES52-wLL0kZm1Gw'
    const body = {
        query: `
            query GetMenu($locale: SiteLocale) {
                ${key}(locale: $locale, filter:{id:{eq: "${id}"}}) {
					id
					title
                    mobileBottomBarText
                    contactButtonText
					contactButtonLink {
						... on ${DESIGN_CONTENT_TYPES.SINGLE_PAGE} {
							slug
						}
					}
					contactHeader
					socialsHeader
					menuItems {
						id
						title
						disableMenuItem
						link {
							... on ${DESIGN_CONTENT_TYPES.ALL_PROJECTS_PAGE} {
								slug
								title
								menuContext {
									previewText
									secondText
									icon
									disableCarouselOnHover 
									previewItems {
										__typename
										... on ${DESIGN_CONTENT_TYPES.SINGLE_PAGE} {
											slug
											menuPreviewImage {
												height
												width
												url
												alt
											}
										}
										... on ${DESIGN_CONTENT_TYPES.SINGLE_PROJECT} {
											slug
											menuPreviewImage {
												height
												width
												url
												alt
											}
										}										
										... on ${DESIGN_CONTENT_TYPES.SINGLE_HOW_WE_WORK_PAGE_NEW} {
											slug
											menuPreviewImage {
												height
												width
												url
												alt
											}
										}
										... on ${COMMON_CONTENT_TYPES.SERVICE_PAGE_RECORD} {
											slug
											menuPreviewImage {
												height
												width
												url
												alt
											}
										}
										... on ${COMMON_CONTENT_TYPES.COMPETENCES_PAGE_RECORD} {
											slug
											menuPreviewImage {
												height
												width
												url
												alt
											}
										}
									}
								}
							}
							... on ${DESIGN_CONTENT_TYPES.SINGLE_PAGE} {
								slug
								menuContext {
									previewText
									secondText
									icon
									disableCarouselOnHover
									previewItems {
										__typename
										... on ${DESIGN_CONTENT_TYPES.SINGLE_PAGE} {
											slug
											menuPreviewImage {
												height
												width 
												url 
												alt
											}
										}
										... on ${COMMON_CONTENT_TYPES.SERVICE_PAGE_RECORD} {
											slug
											menuPreviewImage {
												height
												width
												url
												alt
											}
										}
										... on ${COMMON_CONTENT_TYPES.COMPETENCES_PAGE_RECORD} {
											slug
											menuPreviewImage {
												height
												width
												url
												alt
											}
										}
										... on ${DESIGN_CONTENT_TYPES.SINGLE_PROJECT} {
											slug
											menuPreviewImage {
												height
												width
												url
												alt
											}
										}								
										... on ${DESIGN_CONTENT_TYPES.SINGLE_HOW_WE_WORK_PAGE_NEW} {
											slug
											menuPreviewImage { 
												height
												width
												url
												alt
											}
										}
									}
								}
							}
							... on GroupSinglePageRecord {
								id
								slug
								menuContext {
									__typename
									id
									disableCarouselOnHover
									previewText
									previewItems {
										... on CommonBlogPageRecord {
											__typename
											id
											blogPageName
											slug
											pleoCategory
											thumbnailImage {
												url
												alt
												width
												height
												focalPoint {
													x
													y
												}										
											}
									  	}
									}
								}
							}
						}
					}
                }
            }
        `,
        variables: {locale},
    };

    return await fetchDataAndCache(cacheKey, input, method, headers, next, body, key, false);
}

export async function getCookies(locale) {
    const key = 'allCookiesLists';
    const cacheKey = `${key}-${locale}`;
    const body = {
        query: `
            query GetCookies($locale: SiteLocale) {
            	${key}(locale: $locale, filter: { pleoType: { eq: "${PLEO_TYPE.DESIGN}" } }) {
                    title
                    header
                    description
                    backButton
                    configButton
                    acceptAllButton
                    acceptSelectedButton
                    generalButton
                    generalDescription
                    image {
                    	url
                    	alt
					}
                    addCookies {
                        cookieName
                        cookieType
                        cookieDescription
                    }
                }
            }
        `,
        variables: {locale},
    };

    return await fetchDataAndCache(cacheKey, input, method, headers, next, body, key, false);
}

async function getFooter(locale) {
    const key = 'footer';
    const id = 'M5dAuGbbQNuJQ16CpAYTCw'
    const cacheKey = `${key}-${locale}`;
    const body = {
        query: `
            query GetFooter($locale: SiteLocale) {
                ${key}(locale: $locale, filter: { id: { eq: "${id}" } }) {
					id
					tagInfo: info
					tagLink: link{
						text
						url
					}
					links {
						... on ${DESIGN_CONTENT_TYPES.SINGLE_PAGE} {
							slug
							name
						}
						... on ${DESIGN_CONTENT_TYPES.ALL_PROJECTS_PAGE} {
							slug
							name
						}
					}
					logos {
						id
						logoLink
						logo {
							url
							alt
							width
							height
						}
					}
		  		}
            }
            
            
        `,
        variables: {locale},
    };

    return await fetchDataAndCache(cacheKey, input, method, headers, next, body, key, false);
}


export async function getLayoutData(locale) {
    const globals = await getGlobals(locale);
    const cookies = await getCookies(locale);
    const footer = await getFooter(locale);
    const menu = await getMenu(locale);

    return {
        globals,
        cookies,
        footer,
        menu
    }
}

